
import React, {useState} from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import { AppNotification } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { 
    X, 
    CheckCircle2, 
    UserPlus, 
    MessageSquare, 
    AlertCircle, 
    Layers,
    Trash2,
    Check,
    Loader2
} from 'lucide-react';

const NotificationCenter: React.FC<{ isOpen: boolean, onClose: () => void, isCompact?: boolean }> = ({ isOpen, onClose, isCompact = false }) => {
    const { 
        notifications, 
        markNotificationAsRead, 
        markAllNotificationsAsRead, 
        clearAllNotifications, 
        openDetailModal, 
        tasks, 
        showToast,
        track 
    } = useStandUp();
    const [isMarkingRead, setIsMarkingRead] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    // Track when notification center is opened
    React.useEffect(() => {
        if (isOpen) {
            track('opened_notification_center', { unreadCount: notifications.filter(n => !n.isRead).length });
        }
    }, [isOpen, track]);

    if (!isOpen) return null;

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const handleMarkAllRead = async () => {
        setIsMarkingRead(true);
        track('notifications_mark_all_read');
        try {
            await markAllNotificationsAsRead();
            showToast("All notifications marked as read");
        } finally {
            setIsMarkingRead(false);
        }
    };

    const handleClearAll = async () => {
        setIsClearing(true);
        track('notifications_clear_all');
        try {
            await clearAllNotifications();
            showToast("Notifications cleared");
        } finally {
            setIsClearing(false);
        }
    };

    const handleNotifClick = (n: AppNotification) => {
        track('notification_clicked', { type: n.type, taskId: n.taskId });
        if (!n.isRead) markNotificationAsRead(n.id);
        
        // Find the task and open detail modal
        const task = tasks.find(t => t.id === n.taskId);
        if (task) {
            openDetailModal(task);
            onClose();
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'assignment': return <UserPlus size={14} className="text-blue-500" />;
            case 'note': return <MessageSquare size={14} className="text-emerald-500" />;
            case 'block': return <AlertCircle size={14} className="text-red-500" />;
            case 'dependency': return <Layers size={14} className="text-purple-500" />;
            default: return <CheckCircle2 size={14} className="text-gray-500" />;
        }
    };

    return (
        <div className={`absolute ${isCompact ? 'right-[-8px]' : 'right-0'} top-full mt-2 ${isCompact ? 'w-[280px]' : 'w-80'} bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 z-[100] flex flex-col max-h-[500px] overflow-hidden animate-in slide-in-from-top-2 duration-300`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900 dark:text-white">Notifications</h3>
                    {unreadCount > 0 && (
                        <span className="bg-brand-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {notifications.length > 0 && (
                        <>
                            <button 
                                onClick={handleMarkAllRead}
                                disabled={isMarkingRead}
                                className="p-1.5 text-gray-400 hover:text-brand-500 transition-colors disabled:opacity-50"
                                title="Mark all as read"
                            >
                                {isMarkingRead ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            </button>
                            <button 
                                onClick={handleClearAll}
                                disabled={isClearing}
                                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                                title="Clear all"
                            >
                                {isClearing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                            </button>
                        </>
                    )}
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {notifications.length === 0 ? (
                    <div className="py-12 text-center flex flex-col items-center gap-3">
                        <div className="w-12 h-12 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-300">
                            <CheckCircle2 size={24} />
                        </div>
                        <p className="text-sm text-gray-400 font-medium italic">All caught up!</p>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {notifications.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => handleNotifClick(n)}
                                className={`flex items-start gap-3 p-4 border-b border-gray-50 dark:border-gray-700/50 text-left transition-all hover:bg-gray-50 dark:hover:bg-gray-700/30 relative ${!n.isRead ? 'bg-brand-50/30 dark:bg-gray-700/20' : ''}`}
                            >
                                {!n.isRead && (
                                    <div className="absolute left-1 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full" />
                                )}
                                
                                <img 
                                    src={n.senderPhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent(n.senderName)}`} 
                                    className="w-8 h-8 rounded-full border border-gray-100 dark:border-gray-700 shadow-sm shrink-0 mt-0.5" 
                                    alt="" 
                                />
                                
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                        <span className="font-bold text-xs text-gray-900 dark:text-white truncate">{n.senderName}</span>
                                        <div className="p-1 bg-white dark:bg-gray-800 rounded-md shadow-xs border border-gray-100 dark:border-gray-700">
                                            {getIcon(n.type)}
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-snug">
                                        {n.text}
                                    </p>
                                    <div className="mt-1 flex items-center gap-2">
                                        <span className="text-[10px] font-mono font-black text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/30 px-1 rounded truncate max-w-[80px]">
                                            {n.taskKey || 'TASK'}
                                        </span>
                                        <span className="text-[9px] text-gray-400 font-medium uppercase">
                                            {formatDistanceToNow(n.createdAt?.toDate ? n.createdAt.toDate() : new Date(n.createdAt), { addSuffix: true })}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationCenter;
